/* SHARING A PROJECT, AND TAKING TURNS EDITING IT.
 *
 * A project can be given to other people by email address, and exactly one of
 * them at a time may edit it. Everything this module talks to is defined in
 * supabase/migrations/0024_shared_projects.sql, and the rules are enforced
 * there rather than here: a client that ignored every function below could
 * still not read a project it was not given, and could not save one somebody
 * else is holding. This is the polite face of that, not the security.
 *
 * WHY TAKING TURNS RATHER THAN EDITING TOGETHER. services/projects.js writes
 * the whole document in one statement, guarded on `rev`. Two people editing at
 * once would mean one of them losing an afternoon to the other's save, and a
 * 3D scene is not something two edits merge into. So the project is shared and
 * the editing is taken in turns, which is how the work moves between people
 * anyway -- one person builds it, hands it over, someone else picks it up.
 */

import { requireClient, isConfigured } from './supabase.js';
import { currentUser, isSignedIn } from './auth.js';
import { emit } from '../state/bus.js';

/* Long enough that nobody loses their turn while thinking, short enough that a
   closed laptop frees the project before anyone needs to ask. The heartbeat
   runs at a third of it, so two missed beats still leave room to recover. */
const LOCK_MINUTES = 15;
const HEARTBEAT_MS = 5 * 60 * 1000;

let beat = null;
let held = null;          // project id whose lock this tab is keeping alive

/* ── who a project is shared with ────────────────────────────────────────── */

/** Every address a project is shared with. The owner sees all of them; anyone
 *  else sees only the row naming themselves, which is the database's doing. */
export async function listShares(projectId) {
  if (!isConfigured || !isSignedIn()) return [];
  const { data, error } = await requireClient()
    .from('project_shares')
    .select('email, created_at')
    .eq('project_id', projectId)
    .order('created_at');
  if (error) throw error;
  return data;
}

/** Share with an address. Deliberately not checked against a list of accounts:
 *  the row is keyed on email precisely so a project can be shared with someone
 *  before they have signed up, and it starts working the moment they do. */
export async function addShare(projectId, email) {
  const address = String(email || '').trim().toLowerCase();
  if (!address || !address.includes('@')) throw new Error('That does not look like an email address.');
  if (address === (currentUser()?.email || '').toLowerCase())
    throw new Error('That is your own address — you already have this project.');

  const { error } = await requireClient()
    .from('project_shares')
    .insert({ project_id: projectId, email: address });
  /* 23505 is the primary key: already shared with them. Saying so is friendlier
     than a constraint name, and it is not an error worth stopping for. */
  if (error && error.code !== '23505') throw error;
  return address;
}

export async function removeShare(projectId, email) {
  const { error } = await requireClient()
    .from('project_shares')
    .delete()
    .eq('project_id', projectId)
    .eq('email', String(email).toLowerCase());
  if (error) throw error;
}

/** Whether anyone at all has this project besides its owner -- the question
 *  that decides whether turns are taken at all. One row is enough to know, and
 *  both the owner and a collaborator can see at least one if there is one. */
export async function isShared(projectId) {
  if (!isConfigured || !isSignedIn()) return false;
  const { data, error } = await requireClient()
    .from('project_shares')
    .select('project_id')
    .eq('project_id', projectId)
    .limit(1);
  if (error) throw error;
  return data.length > 0;
}

/* ── taking a turn ───────────────────────────────────────────────────────── */

/**
 * Ask for the editing turn.
 *
 * Returns { ok: true, until } when it is yours, or { ok: false, by, until }
 * naming whoever has it. One function for taking and for keeping: re-asking
 * while you hold it is exactly a heartbeat, and the database treats it that
 * way, so there is no second call that could drift out of step with this one.
 */
export async function claimLock(projectId) {
  const { data, error } = await requireClient()
    .rpc('claim_project_lock', { p_id: projectId, p_minutes: LOCK_MINUTES });
  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  if (row) {
    startHeartbeat(projectId);
    return { ok: true, until: row.lock_expires_at };
  }

  /* Refused: somebody else is editing. Ask who, so the answer can name them.
     A separate read because the claim returns nothing at all when it loses --
     that is what "no rows updated" means -- and "someone else" is a worse
     thing to tell a person than a colleague's address. */
  const holder = await lockHolder(projectId);
  return { ok: false, by: holder?.email || null, until: holder?.until || null };
}

/** Who is editing, if anyone. Null when the project is free. */
export async function lockHolder(projectId) {
  const { data, error } = await requireClient()
    .from('projects')
    .select('locked_by, locked_by_email, lock_expires_at')
    .eq('id', projectId)
    .maybeSingle();
  if (error || !data) return null;
  if (!data.locked_by || !data.lock_expires_at) return null;
  if (new Date(data.lock_expires_at) <= new Date()) return null;   // lapsed
  return {
    userId: data.locked_by,
    email: data.locked_by_email || null,
    until: data.lock_expires_at,
    isMine: data.locked_by === currentUser()?.id
  };
}

/** Give the turn back. Safe to call when this tab never had it. */
export async function releaseLock(projectId) {
  stopHeartbeat();
  if (!projectId || !isConfigured || !isSignedIn()) return;
  try {
    await requireClient().rpc('release_project_lock', { p_id: projectId });
  } catch {
    /* Losing the turn is not worth interrupting anyone over: it expires by
       itself, which is the whole reason it has an expiry. */
  }
}

export const holdsLock = (projectId) => held === projectId;

/* ── keeping the turn ────────────────────────────────────────────────────── */

function startHeartbeat(projectId) {
  if (held === projectId && beat) return;
  stopHeartbeat();
  held = projectId;
  beat = setInterval(() => {
    requireClient()
      .rpc('claim_project_lock', { p_id: projectId, p_minutes: LOCK_MINUTES })
      .then(({ data }) => {
        /* Nothing back means the turn was taken -- the tab slept past the
           expiry and somebody else picked the project up. Saying so now, rather
           than leaving it for the next save to discover, is the difference
           between "you cannot save this" and "you cannot save the last twenty
           minutes of this". */
        const row = Array.isArray(data) ? data[0] : data;
        if (!row) { stopHeartbeat(); emit('share:turn-lost', { projectId }); }
      })
      .catch(() => {});
  }, HEARTBEAT_MS);
}

function stopHeartbeat() {
  if (beat) clearInterval(beat);
  beat = null;
  held = null;
}

/* A closing tab should hand the project back rather than make the next person
   wait out the expiry. sendBeacon is not available for an authenticated PostgREST
   call, so this is best-effort and the expiry remains the thing that is actually
   relied on. */
addEventListener('pagehide', () => {
  if (held) {
    const id = held;
    stopHeartbeat();
    try {
      requireClient().rpc('release_project_lock', { p_id: id });
    } catch { /* the tab is going away; the expiry covers this */ }
  }
});
